/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable class-methods-use-this */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { CmInputComponent } from 'form-controls';
import { finalize, takeUntil } from 'rxjs/operators';
import { TotpSetupResponse } from 'src/app/models/mfa/mfa.model';
import { IsUserRegistered } from 'src/app/models/register/is-user-registered.model';
import { RegisterPayload } from 'src/app/models/register/register-payload.model';
import { RegisterService } from 'src/app/services/general/register.service';
import { MetaService } from 'src/app/services/meta.service';
import { ComponentDestroyClass } from 'src/app/shared/classes/component-destroy.class';
import {
  checkLoginMail,
  checkPasswords,
  checkPasswordSpaces,
  customPatternValidator,
  customRequiredValidator,
  isValidPin,
} from 'src/app/shared/utils/form-validators';
import { AuthTypeValue } from '../../models/app-config/auth-type-value.model';
import { AccessRulesService } from '../../services/access-rules.service';
import { PrivatePolicyComponent } from './modals/private-policy/private-policy.component';
import { TermsUseAgreementComponent } from './modals/terms-use-agreement/terms-use-agreement.component';
import { TutorialComponent } from './modals/tutorial/tutorial.component';

@Component({
  standalone: false,
  selector: 'cms-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent extends ComponentDestroyClass implements OnInit, OnDestroy {
  @ViewChild('passwordControl', { static: false }) passwordControl: CmInputComponent;

  @ViewChild('repeatpassControl', { static: false }) repeatpassControl: CmInputComponent;

  isRegistered: boolean;

  form: FormGroup;

  loading = false;

  error: string = null;

  passwordVisibility = false;

  repeatpassVisibility = false;

  showPasswordValidationRules = false;

  /** Step 1 — account details; step 2 — authenticator setup (new users only). */
  step: 1 | 2 = 1;

  totpSetup: TotpSetupResponse = null;

  /** TOTP is turned off on the Security page — registration is a single step without an authenticator. */
  totpDisabled = false;

  totpSetupLoading = false;

  /** Spinner on "Next" — only when the user pressed it, not during the check on page load. */
  nextLoading = false;

  private readonly routeQueryParams: Params;

  private readonly dataRegistered: IsUserRegistered;

  private readonly REQUIRED_MESSAGE = 'The field should be filled in';

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private metaSvc: MetaService,
    private notificationSvc: NotificationsService,
    private router: Router,
    private registerSvc: RegisterService,
    private accessRulesService: AccessRulesService,
    private sanitizer: DomSanitizer,
  ) {
    super();
    this.routeQueryParams = this.route.snapshot.queryParams;
    this.dataRegistered = this.route.snapshot.data.registered;
    this.isRegistered = this.dataRegistered.isUserRegistered;
    const authorizationType = this.accessRulesService.getAuthorizationType();
    this.totpDisabled = !!authorizationType && authorizationType !== AuthTypeValue.TOTP;
  }

  ngOnInit(): void {
    this.checkRegistrationStatus();
    this.metaSvc.setTitle('Registration');
    this.buildForm();
    this.subscribePasswordChange();
    this.formPatchValue();
    this.prefetchTotpSetup();
  }

  ngOnDestroy() {
    super.onDestroy();
  }

  get logo() {
    return this.sanitizeBase64(this.accessRulesService.getLogoBase64());
  }

  get registerBtnDisabled() {
    return this.loading || !this.form.valid;
  }

  get nextBtnDisabled() {
    return (
      this.totpSetupLoading ||
      Object.keys(this.form.controls).some(
        (name) => name !== 'totpCode' && this.form.get(name).invalid,
      )
    );
  }

  get showTotpSetup() {
    return !this.isRegistered && !this.totpDisabled;
  }

  /** Steps are shown only once TOTP is confirmed (QR received), so a single-step flow never shows "Step 1 of 2". */
  get showStepIndicator() {
    return this.showTotpSetup && !!this.totpSetup;
  }

  get totpQrCode() {
    return `data:image/png;base64,${this.totpSetup.qrCodeBase64}`;
  }

  get totpSecretKey() {
    return this.totpSetup.key.match(/.{1,4}/g).join(' ');
  }

  get loginValue(): string {
    return String(this.form.get('login').value || '').trim();
  }

  /** Email comes from the invitation link (the field is read-only), so read it even if disabled. */
  get emailValue(): string {
    return String(this.form.getRawValue().email || '').trim();
  }

  get passwordErrors() {
    return this.form.get('password').errors || {};
  }

  get showRegistrationVideoTutorialLink() {
    return this.accessRulesService.isAllowedByProfileRule(
      this.accessRulesService.getAccessRulesValue().common.register.show
        .registration_video_tutorial_link,
    );
  }

  onSubmit() {
    if (this.showTotpSetup && this.step === 1) {
      this.onNext();
      return;
    }

    if (this.registerBtnDisabled) {
      return;
    }

    this.register();
  }

  onNext() {
    if (this.nextBtnDisabled) {
      return;
    }

    // Always resend: the backend validates the step 1 fields, which may have changed after "Back".
    this.loadTotpSetup(true);
  }

  onBack() {
    this.step = 1;
    this.showPasswordValidationRules = false;
    this.passwordVisibility = false;
    this.repeatpassVisibility = false;
  }

  onChangePasswordVisibility() {
    this.passwordVisibility = !this.passwordVisibility;
    const passwordEl: HTMLInputElement = this.passwordControl.inputElement.nativeElement;
    passwordEl.focus();
    this.putCursorEndText(passwordEl, this.form.get('password'));
  }

  onChangeRepeatpassVisibility() {
    this.repeatpassVisibility = !this.repeatpassVisibility;
    const passwordEl: HTMLInputElement = this.repeatpassControl.inputElement.nativeElement;
    passwordEl.focus();
    this.putCursorEndText(passwordEl, this.form.get('repeatpass'));
  }

  onPasswordFocus() {
    this.showPasswordValidationRules = true;
  }

  onOpenTermsUseAgreement() {
    this.dialog.open(TermsUseAgreementComponent, {
      maxWidth: '600px',
      closeOnNavigation: true,
      data: {
        closeBtn: true,
      },
    });
  }

  onOpenPrivacyPolicy() {
    this.dialog.open(PrivatePolicyComponent, {
      maxWidth: '600px',
      closeOnNavigation: true,
      data: {
        closeBtn: true,
      },
    });
  }

  openRegistrationTutorialModal() {
    this.dialog.open(TutorialComponent, {
      closeOnNavigation: true,
      panelClass: 'modal-video',
    });
  }

  private sanitizeBase64(base64: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(base64);
  }

  private register() {
    this.loading = true;
    const formValue = this.form.value;

    this.registerSvc
      .register(this.formToPayload(formValue))
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (response: any) => {
          this.notificationSvc.success(
            'Success',
            'You are successfully registered! Enter your username and password to login.',
          );

          this.router
            .navigate(['login'])
            .then(() => (this.loading = false))
            .catch((err) => console.error(err));
        },
        error: (err: any) => {
          console.error(err);
          this.notificationSvc.error('Warning', err.message);
          this.loading = false;
        },
      });
  }

  private formToPayload(formValue: any): RegisterPayload {
    return {
      login: formValue.login,
      pin: formValue.pin,
      password: formValue.password,
      email: formValue.email,
      token: this.routeQueryParams.token,
      ...(this.showTotpSetup && { totpCode: String(formValue.totpCode).trim() }),
    };
  }

  /** Finds out on page load whether TOTP is enabled, so the UI shows the right number of steps. */
  private prefetchTotpSetup() {
    if (
      !this.showTotpSetup ||
      !this.routeQueryParams.token ||
      this.dataRegistered.status === 'REGISTERED'
    ) {
      return;
    }

    this.loadTotpSetup(false);
  }

  /** `proceed` — the user pressed "Next": go to step 2, or register right away if TOTP is off. */
  private loadTotpSetup(proceed: boolean) {
    if (!this.routeQueryParams.token) {
      this.notificationSvc.error('Warning', 'Invitation link is invalid.');
      return;
    }

    this.totpSetupLoading = true;
    this.nextLoading = proceed;
    this.registerSvc
      .getTotpSetup(this.routeQueryParams.token, this.loginValue, this.emailValue)
      .pipe(
        finalize(() => {
          this.totpSetupLoading = false;
          this.nextLoading = false;
        }),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe({
        next: (setup: TotpSetupResponse) => {
          this.totpSetup = setup;

          if (proceed) {
            this.step = 2;
          }
        },
        error: (err: any) => {
          if (this.isTotpNotEnabledError(err)) {
            this.disableTotpStep();

            if (proceed) {
              this.register();
            }
            return;
          }

          // On page load other errors (e.g. an expired link) are reported when the user presses "Next".
          if (proceed) {
            this.notificationSvc.error('Warning', err.message);
          }
        },
      });
  }

  /** POST users/register/totp answers 400 with this message when TOTP is turned off on the Security page. */
  private isTotpNotEnabledError(err: any) {
    return err?.status === 400 && /TOTP authentication is not enabled/i.test(err?.message || '');
  }

  private disableTotpStep() {
    this.totpDisabled = true;
    const totpCodeControl = this.form.get('totpCode');
    totpCodeControl.clearValidators();
    totpCodeControl.updateValueAndValidity();
  }

  private checkRegistrationStatus() {
    const registrationStatus = this.dataRegistered.status;

    if (registrationStatus === 'REGISTERED') {
      this.notificationSvc.success(
        'Information',
        'User for this invite has already been registered. Please login.',
      );

      this.router.navigate(['login']);
    } else if (registrationStatus === 'MAIL_SENT' && this.isRegistered) {
      this.router.navigate(['new-client'], { queryParams: this.route.queryParams });
    }
  }

  private buildForm() {
    this.form = this.fb.group({
      login: [
        '',
        {
          validators: [
            customRequiredValidator(this.REQUIRED_MESSAGE),
            Validators.minLength(6),
            Validators.maxLength(24),
            customPatternValidator('^[A-Za-z0-9_.-]{6,24}$', 'Wrong format'),
          ],
        },
      ],
      pin: ['', { validators: [customRequiredValidator(this.REQUIRED_MESSAGE), isValidPin] }],
      check2: [false, { validators: [Validators.requiredTrue] }], // required true
      email: [
        '',
        {
          validators: [
            customRequiredValidator(this.REQUIRED_MESSAGE),
            customPatternValidator(
              /^[a-z0-9!#$%&'*+\/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i,
              'Email is not valid',
            ),
          ],
        },
      ],
      password: [
        '',
        {
          validators: [
            customRequiredValidator(this.REQUIRED_MESSAGE),
            customPatternValidator(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@#!%*?&_^\[\]\\>=<;:/\-,+()'"])([A-Za-z\d$@#!%*?&_^\[\]\\>=<;:/\-,+()'"]*)+$/,
              'Wrong format',
            ),
            Validators.minLength(8),
            Validators.maxLength(24),
            checkLoginMail,
            checkPasswordSpaces,
          ],
        },
      ],
      repeatpass: [
        '',
        { validators: [customRequiredValidator(this.REQUIRED_MESSAGE), checkPasswords] },
      ],
      totpCode: [
        '',
        {
          validators: this.showTotpSetup
            ? [
                customRequiredValidator(this.REQUIRED_MESSAGE),
                customPatternValidator(/^\d{6}$/, 'Code must contain 6 digits'),
              ]
            : [],
        },
      ],
    });
  }

  private putCursorEndText(element: HTMLInputElement, formControl: AbstractControl) {
    const { value } = formControl;
    const len = (value && value.length) || 0;
    setTimeout(() => {
      element.setSelectionRange(len, len);
    }, 0);
  }

  private formPatchValue() {
    let processedEmail = this.routeQueryParams.email || '';

    if (processedEmail) {
      const [localPart, domain] = processedEmail.split('@');
      if (localPart && domain) {
        processedEmail = `${localPart.replace(/ /g, '+')}@${domain}`;
      }
    }

    this.form.patchValue({
      email: processedEmail,
      pin: this.routeQueryParams.pin || '',
    });
  }

  private subscribePasswordChange() {
    this.form
      .get('password')
      .valueChanges.pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.form.get('repeatpass').updateValueAndValidity({ onlySelf: true });
        },
      });
  }
}
